/**
 * Como cada status de documento aparece na tela.
 *
 * Papel: aqui ficam `ROTULOS_STATUS`, que traduz os valores do enum para o que
 * se lê na tabela e no formulário, `STATUS_NA_ORDEM_DO_FORMULARIO`, a ordem em
 * que o formulário oferece os status, e `TOM_DO_STATUS`, que dá a variante de
 * `Badge` de cada status.
 *
 * Como usar: nenhuma tela escreve `Não localizado` por extenso; tudo passa por
 * aqui. O rótulo é um só por status, sem concordância com o documento: o `tipo`
 * é texto livre, e não há como saber o gênero de `CIN` ou de `Passaporte ITA`.
 * A forma é a da lista de status do formulário de origem — `Válido`,
 * `Não aplicável`, `Não localizado`.
 *
 * O tipo `Record<StatusDocumento, …>` é o que obriga a completar estas listas ao
 * acrescentar um valor ao enum: sem o rótulo, o projeto não compila. O nome do
 * documento não passa por aqui — a tela mostra o `tipo` como foi gravado.
 */
import { STATUS_DOCUMENTO, type StatusDocumento } from '@concierge/shared';

/** O status por extenso, como a tabela e o formulário o mostram. */
export const ROTULOS_STATUS: Record<StatusDocumento, string> = {
  valido: 'Válido',
  nao_localizado: 'Não localizado',
  nao_aplicavel: 'Não aplicável',
  sem_informacao: 'Sem informação',
};

/**
 * A posição de cada status na lista do formulário.
 *
 * O `Record` é o que obriga a dar posição a um status novo: sem ela o projeto
 * não compila, em vez de o status sumir da lista em silêncio.
 */
const POSICAO_DO_STATUS: Record<StatusDocumento, number> = {
  valido: 0,
  nao_aplicavel: 1,
  nao_localizado: 2,
  sem_informacao: 3,
};

/**
 * Os status na ordem em que o formulário os oferece.
 *
 * Não é a ordem de `STATUS_DOCUMENTO`, que decide a do enum no banco: é
 * Válido, Não aplicável, Não localizado e Sem informação, a ordem da lista que
 * o escritório já conhece. Trocar esta ordem pela do enum não muda nenhum dado
 * gravado, só embaralha a lista que a pessoa percorre ao preencher.
 */
export const STATUS_NA_ORDEM_DO_FORMULARIO: readonly StatusDocumento[] = [
  ...STATUS_DOCUMENTO,
].sort((a, b) => POSICAO_DO_STATUS[a] - POSICAO_DO_STATUS[b]);

/**
 * A variante de `Badge` de cada status.
 *
 * Só `valido` chega a `concluido`; os outros três — sem localização, sem
 * aplicação ou sem informação — ficam em `pendente`, porque nenhum deles é uma
 * situação encerrada. O `vencido` do documento não vem daqui: é a data que
 * decide, comparada em `jaVenceu`, e aparece como marca à parte ao lado da
 * validade.
 */
export const TOM_DO_STATUS: Record<StatusDocumento, 'pendente' | 'concluido' | 'vencido'> = {
  valido: 'concluido',
  nao_localizado: 'pendente',
  nao_aplicavel: 'pendente',
  sem_informacao: 'pendente',
};
